import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { ReportHeader } from '@/components/reports/ReportUI';
import { ReportFilterBar } from '@/components/reports/ReportFilterBar';
import { useBranchStore } from '@/stores';
import { toast } from 'sonner';
import { TrendingUp, TrendingDown, DollarSign, ArrowDown, ArrowUp, CreditCard, Wallet, Building2, ShoppingCart, Receipt, FileText, BarChart3 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area, Legend } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import api from '@/lib/api';
import { useLanguage } from '@/contexts/LanguageContext';
// PDF export moved to data-driven exports where used
import { ExportButtons } from '@/components/ExportButtons';
import { GlAccountingStrip } from '@/components/accounting/GlAccountingStrip';

const CHART_COLORS = ['#f97316', '#3b82f6', '#22c55e', '#8b5cf6', '#ec4899', '#eab308', '#14b8a6', '#ef4444'];

function formatCurrency(val) {
  return `${(val || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function KPICard({ title, value, icon: Icon, color, subtitle, onClick }) {
  return (
    <Card className={`border-${color}-200 bg-gradient-to-br from-${color}-50 to-white dark:from-${color}-950/20 dark:to-stone-900 ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`} data-testid={`kpi-${title.toLowerCase().replace(/\s/g, '-')}`} onClick={onClick}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold mt-1 text-foreground dark:text-stone-200">{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
            {onClick && <p className="text-[10px] text-primary font-medium mt-1">Click to view details →</p>}
          </div>
          <div className={`w-12 h-12 rounded-xl bg-${color}-100 dark:bg-${color}-900/30 flex items-center justify-center`}>
            <Icon className={`w-6 h-6 text-${color}-600`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function DonutCard({ title, paidPct, outstandingPct, paidAmt, outstandingAmt, color1, color2, onClick }) {
  const data = [
    { name: 'Paid', value: paidPct || 0 },
    { name: 'Outstanding', value: outstandingPct || 0 },
  ];
  if (data[0].value === 0 && data[1].value === 0) data[0].value = 100;
  return (
    <Card
      data-testid={`donut-${title.toLowerCase().replace(/\s/g, '-')}`}
      onClick={onClick}
      className={onClick ? 'cursor-pointer hover:shadow-md hover:border-primary/40 transition-all group' : ''}
      title={onClick ? 'Click to view full list' : undefined}
    >
      <CardHeader className="pb-0 flex flex-row items-center justify-between">
        <CardTitle className="text-sm">{title}</CardTitle>
        {onClick && <span className="text-[10px] text-primary opacity-0 group-hover:opacity-100 transition-opacity font-medium">View All →</span>}
      </CardHeader>
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          <div className="w-32 h-32 relative">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data} dataKey="value" cx="50%" cy="50%" innerRadius={35} outerRadius={55} paddingAngle={3}>
                  <Cell fill={color1} />
                  <Cell fill={color2} />
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-lg font-bold text-foreground">{paidPct}%</span>
            </div>
          </div>
          <div className="flex-1 space-y-3">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ background: color1 }} />
              <div className="flex-1">
                <p className="text-xs text-muted-foreground">Paid</p>
                <p className="text-sm font-bold text-foreground">{formatCurrency(paidAmt)}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ background: color2 }} />
              <div className="flex-1">
                <p className="text-xs text-muted-foreground">Outstanding</p>
                <p className="text-sm font-bold text-foreground">{formatCurrency(outstandingAmt)}</p>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload) return null;
  return (
    <div className="bg-white border border-border rounded-lg shadow-lg p-3 text-sm">
      <p className="font-semibold text-foreground mb-1">{label}</p>
      {payload.map((entry, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-full" style={{ background: entry.color }} />
          <span className="text-muted-foreground capitalize">{entry.name}:</span>
          <span className="font-medium">{formatCurrency(entry.value)}</span>
        </div>
      ))}
    </div>
  );
}

export default function FinancialDashboardPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [dateFilter, setDateFilter] = useState({ start: null, end: null, period: "all" });
  const [branchFilter, setBranchFilter] = useState([]);
  const { branches, fetchBranches } = useBranchStore();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const qs = new URLSearchParams();
        // DateQuickFilter emits YYYY-MM-DD strings directly (see DateQuickFilter.jsx).
        if (dateFilter?.start) qs.append('start_date', dateFilter.start);
        if (dateFilter?.end) qs.append('end_date', dateFilter.end);
        if (branchFilter.length === 1) qs.append('branch_id', branchFilter[0]);
        else if (branchFilter.length > 1) qs.append('branch_ids', branchFilter.join(','));
        const q = qs.toString();
        const res = await api.get(`/accounting/financial-dashboard${q ? '?' + q : ''}`);
        setData(res.data);
      } catch { toast.error('Failed to load financial dashboard'); }
      setLoading(false);
    };
    fetchData();
  }, [dateFilter?.start, dateFilter?.end, branchFilter]);

  if (loading && !data) return (
    <DashboardLayout>
      <div className="p-6 flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    </DashboardLayout>
  );

  if (!data) return (
    <DashboardLayout>
      <div className="p-6 text-center text-muted-foreground">Failed to load data</div>
    </DashboardLayout>
  );

  const cashFlowIsPositive = data.cash_flow.net >= 0;
  const totals = data.totals || {};
  const pnl = data.pnl_summary || {};
  const inv = data.invoices_status || {};
  const bills = data.bills_status || {};

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="financial-dashboard-page">
        <ReportHeader icon={BarChart3} title={<span data-testid="page-title">Financial Dashboard</span>} subtitle="Complete financial overview for your business" testId="fd-header"
          actions={loading ? <span className="text-xs text-muted-foreground" data-testid="fd-refreshing">Refreshing…</span> : null} />
        <ReportFilterBar testId="fd-filter" branches={branches} branchIds={branchFilter} onBranchIds={setBranchFilter} allowAll defaultPreset="all"
          range={dateFilter?.start ? dateFilter : null} onRange={(r) => setDateFilter({ start: r?.start || null, end: r?.end || null, period: r ? 'custom' : 'all' })}>
          <ExportButtons dataType="financial_dashboard" label="Financial Dashboard" startDate={dateFilter?.start} endDate={dateFilter?.end} branchIds={branchFilter} />
        </ReportFilterBar>

        {/* VÔM-style Top KPIs */}
        <div id="financial-dashboard-content">
        <div className="mb-3"><GlAccountingStrip startDate={data.range_start || dateFilter?.start} endDate={data.range_end || dateFilter?.end} branchIds={branchFilter} operational={data?.pnl_summary?.net_income} operationalLabel="Operational net profit" /></div>
        {data.range_label && (
          <p className="text-[11px] text-muted-foreground mb-2" data-testid="fd-range-label">
            Showing: <span className="font-medium text-foreground">{data.range_label}</span>
            {!data.custom_range && ' — apply a date filter above to narrow to a specific period'}
          </p>
        )}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <KPICard title="Total Sales" value={formatCurrency(totals.total_sales)} icon={ShoppingCart} color="emerald" subtitle={data.custom_range ? 'Selected period' : 'Last 6 months'} onClick={() => navigate('/sales')} />
          <KPICard title="Total Expenses" value={formatCurrency(totals.total_expenses)} icon={Receipt} color="red" subtitle={data.custom_range ? 'Selected period' : 'Last 6 months'} onClick={() => navigate('/expenses')} />
          <KPICard title="Net Cash Flow" value={formatCurrency(data.cash_flow.net)} icon={DollarSign} color={cashFlowIsPositive ? 'blue' : 'red'} subtitle={cashFlowIsPositive ? 'Positive' : 'Negative'} onClick={() => navigate('/cash-flow')} />
          <KPICard title="Receivable" value={formatCurrency(data.outstanding.receivable)} icon={CreditCard} color="orange" subtitle={`${data.outstanding.receivable_count} pending`} onClick={() => navigate('/aging-report')} />
          <KPICard title="Payable" value={formatCurrency(data.outstanding.payable)} icon={Wallet} color="purple" subtitle={`${data.outstanding.payable_count} bills`} onClick={() => navigate('/supplier-report')} />
        </div>

        {/* P&L Summary + Invoice/Bills Donuts (VÔM-style) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Net Income Card */}
          <Card
            className="border-2 border-border cursor-pointer hover:shadow-md hover:border-primary/40 transition-all group"
            onClick={() => navigate('/profit-loss')}
            data-testid="pnl-card-clickable"
            title="Click to open the full Profit & Loss report"
          >
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-sm">Profit & Loss</CardTitle>
              <span className="text-[10px] text-primary opacity-0 group-hover:opacity-100 transition-opacity font-medium">View Report →</span>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="text-center mb-3">
                <p className="text-xs text-muted-foreground">Net Income</p>
                <p className={`text-3xl font-bold ${pnl.net_income >= 0 ? 'text-success' : 'text-error'}`} data-testid="pnl-net-income">
                  {formatCurrency(pnl.net_income)}
                </p>
              </div>
              <div className="space-y-2">
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-muted-foreground">Income</span>
                    <span className="font-medium text-success">{formatCurrency(pnl.income)}</span>
                  </div>
                  <div className="h-2.5 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-success rounded-full" style={{ width: `${Math.min(100, Math.max(pnl.income, pnl.expenses) > 0 ? (pnl.income / Math.max(pnl.income, pnl.expenses) * 100) : 0)}%` }} />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-muted-foreground">Expenses</span>
                    <span className="font-medium text-error">{formatCurrency(pnl.expenses)}</span>
                  </div>
                  <div className="h-2.5 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-error rounded-full" style={{ width: `${Math.min(100, Math.max(pnl.income, pnl.expenses) > 0 ? (pnl.expenses / Math.max(pnl.income, pnl.expenses) * 100) : 0)}%` }} />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Invoices Donut */}
          <DonutCard title="Invoices" paidPct={inv.paid_pct} outstandingPct={inv.outstanding_pct} paidAmt={inv.total_paid} outstandingAmt={inv.total_outstanding} color1="#22c55e" color2="#f97316" onClick={() => navigate('/invoices')} />

          {/* Bills Donut */}
          <DonutCard title="Purchase Bills" paidPct={bills.paid_pct} outstandingPct={bills.outstanding_pct} paidAmt={bills.total_paid} outstandingAmt={bills.total_outstanding} color1="#3b82f6" color2="#ef4444" onClick={() => navigate('/bills')} />
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Revenue & Expense Trend */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Revenue vs Expenses (6 Months)</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={data.revenue_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#a8a29e" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#a8a29e" tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                  <Area type="monotone" dataKey="revenue" name="Revenue" stroke="#22c55e" fill="#22c55e" fillOpacity={0.15} strokeWidth={2} />
                  <Area type="monotone" dataKey="expenses" name="Expenses" stroke="#ef4444" fill="#ef4444" fillOpacity={0.1} strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Profit Trend */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Net Profit Trend (6 Months)</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={data.revenue_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#a8a29e" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#a8a29e" tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="profit" name="Net Profit" radius={[4, 4, 0, 0]}>
                    {data.revenue_trend.map((entry, i) => (
                      <Cell key={i} fill={entry.profit >= 0 ? '#22c55e' : '#ef4444'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Bottom Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Expense Breakdown */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Expense Breakdown (This Month)</CardTitle></CardHeader>
            <CardContent>
              {data.expense_breakdown.length === 0 ? (
                <div className="flex items-center justify-center h-64 text-muted-foreground text-sm">No expenses this month</div>
              ) : (
                <div className="flex gap-4">
                  <ResponsiveContainer width="50%" height={250}>
                    <PieChart>
                      <Pie data={data.expense_breakdown} dataKey="amount" nameKey="category" cx="50%" cy="50%" outerRadius={90} innerRadius={50} paddingAngle={3}>
                        {data.expense_breakdown.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={v => formatCurrency(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex-1 space-y-2 overflow-y-auto max-h-[250px]">
                    {data.expense_breakdown.map((cat, i) => (
                      <div key={cat.category} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                          <span className="text-muted-foreground">{cat.category}</span>
                        </div>
                        <span className="font-medium text-foreground">{formatCurrency(cat.amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Payment Method Breakdown */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Revenue by Payment Method</CardTitle></CardHeader>
            <CardContent>
              {data.payment_breakdown.length === 0 ? (
                <div className="flex items-center justify-center h-64 text-muted-foreground text-sm">No revenue this month</div>
              ) : (
                <div className="space-y-4">
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={data.payment_breakdown} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis type="number" tick={{ fontSize: 11 }} stroke="#a8a29e" tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                      <YAxis type="category" dataKey="method" tick={{ fontSize: 11 }} stroke="#a8a29e" width={80} />
                      <Tooltip formatter={v => formatCurrency(v)} />
                      <Bar dataKey="amount" name="Amount" radius={[0, 4, 4, 0]}>
                        {data.payment_breakdown.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Monthly Summary Table */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Monthly Summary</CardTitle></CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 dark:bg-foreground">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Month</th>
                    <th className="text-right px-4 py-3 font-medium text-muted-foreground">Revenue</th>
                    <th className="text-right px-4 py-3 font-medium text-muted-foreground">Expenses</th>
                    <th className="text-right px-4 py-3 font-medium text-muted-foreground">Profit/Loss</th>
                    <th className="text-right px-4 py-3 font-medium text-muted-foreground">Sales</th>
                  </tr>
                </thead>
                <tbody>
                  {data.revenue_trend.map(m => (
                    <tr
                      key={m.month}
                      className="border-t border-border hover:bg-primary/5 cursor-pointer transition-colors group"
                      onClick={() => navigate(`/profit-loss?period=${encodeURIComponent(m.month)}`)}
                      title={`Open Profit & Loss for ${m.month}`}
                      data-testid={`fd-monthly-row-${m.month}`}
                    >
                      <td className="px-4 py-3 font-medium text-foreground dark:text-stone-200 group-hover:text-primary transition-colors">{m.month}</td>
                      <td className="px-4 py-3 text-right text-success font-medium">{formatCurrency(m.revenue)}</td>
                      <td className="px-4 py-3 text-right text-error font-medium">{formatCurrency(m.expenses)}</td>
                      <td className={`px-4 py-3 text-right font-bold ${m.profit >= 0 ? 'text-success' : 'text-error'}`}>{formatCurrency(m.profit)}</td>
                      <td className="px-4 py-3 text-right text-muted-foreground">{m.sales_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
